import { PaymentMethods } from "../../../../common/constants/payment_methods";
import { inDollars } from "../../../../../DriveScout/Billing/Teller";
import { saveToDisk } from "../../../../common/helpers/saveToDisk";
import { formatDateTime, endOfMonth } from "../../../../common/helpers/time";
import {
  PaymentOutcome,
  PaymentRequest,
} from "../../../../common/payments/PaymentRequest";

class OrderReportsController {
  constructor(
    OrdersModel,
    UserService,
    OrderDetailsService,
    PoliciesModel,
    $q,
    FlashService,
    UsersModel,
  ) {
    this.OrdersModel = OrdersModel;
    this.OrderDetailsService = OrderDetailsService;
    this.PoliciesModel = PoliciesModel;
    this.$q = $q;
    this.FlashService = FlashService;
    this.UsersModel = UsersModel;
    this.payment_methods = [...PaymentMethods];
    this.reportTotalPaid = 0;
    this.payments = [];
    this.viewing_order = 0;
    this.payment_methods.unshift({
      name: "All",
      value: "all",
    });
    this.filter = {
      payment_method: this.payment_methods[0],
      startDate: new Date(),
      endDate: endOfMonth(new Date()), // DS-PORT-DEFECT (drivescout-vue3 M4 slice 3, 2026-09-28): row P-report-window: the default window is today through the end of this month, so the first load shows only today's payments; reproduced in the port.
    };
    this.inDollars = inDollars.bind(this);

    this.loading = true;
    this.selectionHasStudents = false;

    this.applyingFilter = false;
    this.modalShown = false;
    this.order = {}; //used for order details modal
    this.billingConfig = {}; //used for order details modal
    this.clearPaymentForm = false; //change to true after form submit to clear vue form
    // OrderDetail.vue only emits the payment; this screen sends it and sees the answer, so it holds the key.
    this.orderPayment = new PaymentRequest();

    this.updateStartDate = this.updateStartDate.bind(this);
    this.updateEndDate = this.updateEndDate.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.submitPayment = this.submitPayment.bind(this);
    this.voidPayment = this.voidPayment.bind(this);
    this.voidOrder = this.voidOrder.bind(this);

    this.currentUser = UserService.getCurrentUser();

    OrdersModel.reports(this.getFilterParams(this.filter)).then((response) => {
      this.payments = response.data.payments;
      this.loading = false;
      if (this.payments.length > 0) {
        this.reportTotalPaid = this.payments.reduce((carry, payment) => {
          return carry + parseInt(payment.amount);
        }, 0);
      }
    });
  }

  showOrderDetails(payment) {
    this.loading = true;
    this.viewing_order = payment.order_id;
    this.$q
      .all([
        this.PoliciesModel.getByID("billing"),
        this.OrdersModel.getDetails({ id: payment.order_id }),
      ])
      .then((responses) => {
        this.order = this.OrderDetailsService.setupOrderDetails(
          responses[1],
          payment,
        );
        this.billingConfig = this.OrderDetailsService.configureBilling(
          responses[0],
          payment,
        );
        this.clearPaymentForm = false;
        this.modalShown = true;
        this.loading = false;
      });
  }

  closeModal() {
    this.modalShown = false;
    this.order = {};
    this.viewing_order = null;
    this.clearPaymentForm = true;
  }

  getFilterParams() {
    const filter = Object.assign({}, this.filter);
    return {
      payment_method: filter.payment_method.value,
      start_date: formatDateTime(filter.startDate, "YYYY-MM-DD"),
      end_date: formatDateTime(filter.endDate, "YYYY-MM-DD"),
    };
  }

  submitFilter() {
    this.reportTotalPaid = 0;
    this.loading = true;
    this.OrdersModel.reports(this.getFilterParams(this.filter)).then(
      (response) => {
        this.payments = response.data.payments;
        this.loading = false;
        if (this.payments.length > 0) {
          this.reportTotalPaid = this.payments.reduce((carry, payment) => {
            return carry + parseInt(payment.amount);
          }, 0);
        }
      },
    );
  }

  submitPayment(data) {
    const payment = data.payment;
    this.submitted = true;
    this.OrderDetailsService.setPayment(payment);
    this.OrderDetailsService.submitPayment(this.orderPayment) // DS-PORT-DEFECT (drivescout-vue3 M4 slice 3, 2026-09-28): row F-3ds-modal: the modal's pay path has no 3DS branch, so a 202 requires_action would flash a blank success, push undefined onto payments and throw at response.order.paid with the PaymentIntent unconfirmed (latent: OrderDetail.vue's raw card form cannot reach a Stripe 202); fixed in the port (the modal hosts the shared order view).
      .then((response) => {
        this.orderPayment.settled(PaymentOutcome.answered(response));
        this.FlashService.setMessage({
          type: "success",
          message: response.message,
        });
        this.order.payments.push(response.payment);
        this.submitted = false;
        this.order.paid = response.order.paid;
        this.order.notes = response.order.notes;
        this.clearPaymentForm = true;
      })
      .catch((error) => {
        this.orderPayment.settled(PaymentOutcome.failed(error));
        this.submitted = false;
      });
  }

  updateStartDate(newDate) {
    this.filter.startDate = newDate.selected[0];
  }

  updateEndDate(newDate) {
    this.filter.endDate = newDate.selected[0];
  }

  voidPayment(data) {
    const { payment_id, index } = data;
    this.submitted = true;
    this.UsersModel.voidPayment(this.order.student.id, payment_id)
      .then((response) => {
        this.submitted = false;
        this.FlashService.setMessage({
          type: "success",
          message: response.message,
        });
        this.order.payments.splice(index, 1, response.payment);
      })
      .catch((Error) => {
        this.submitted = false;
      });
  }

  voidOrder(data) {
    this.submitted = true;
    this.OrdersModel.voidOrder(data.order_id)
      .then((response) => {
        this.submitted = false;
        this.FlashService.setMessage({
          type: "success",
          message: response.message,
        });
        this.order.status = "VOID";
      })
      .catch((Error) => {
        this.submitted = false;
      });
  }


  sendOrder(data) {
    this.submitting = true;
    this.OrdersModel.sendOrder(data.order_id, { recipient: data.receipt_email })
      .then((response) => {
        this.submitting = false;
        this.FlashService.setMessage({
          type: "success",
          message: response.message,
        });
      })
      .catch((Error) => {
        this.submitting = false;
      });
  }

  printOrder(data) {
    this.submitting = true;
    this.OrdersModel.printOrder(data.order_id)
      .then((response) => {
        this.submitting = false;
        this.FlashService.setMessage({
          type: "success",
          message: response.message,
        });
        saveToDisk(response.url);
      })
      .catch((Error) => {
        this.submitting = false;
      });
  }
}

OrderReportsController.$inject = [
  "OrdersModel",
  "UserService",
  "OrderDetailsService",
  "PoliciesModel",
  "$q",
  "FlashService",
  "UsersModel",
];

export default OrderReportsController;

